import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDisclosure } from "@chakra-ui/react";
import { AlertDialog } from "@components";
import { Button } from "@chakra-ui/react";
import {
  updateSelectedActivity,
  addToGracePeriod,
  removeFromGracePeriod,
} from "@store/activitySlice";
import {
  useGoLiveMutation,
  useStopLiveMutation,
  useRemixActivityMutation,
  useDeleteActivityMutation,
  useUpdateActivityMutation,
} from "@store/api/activities";
import { useToastNotification } from "@hooks";
import { activityTitles, activityTypes } from "@constants/activities";
import ActivityCardUI from "./ActivityCardUI";
import InsightModal from "./InsightModal";
import { RemixActivityModal } from "@components/Activity";
import RemixLimitModal from "../Modals/RemixLimitModal";
import {
  selectMeetingParticipants,
  selectMeetingHost,
} from "@store/selectors/meetingSelectors";

// Helper function to get activity icon emoji based on type (fallback only)
const getActivityIcon = (type) => {
  switch (type) {
    case "poll":
      return "📊";
    case "quiz":
      return "🧠";
    case "share":
      return "💬";
    case "thoughts":
      return "⏰";
    default:
      return "🎯";
  }
};

// Helper function to get the display icon - use emoji from activity data or fallback
const getDisplayIcon = (activity) => {
  return activity.emoji || getActivityIcon(activity.type);
};

/**
 * Container component that handles all Redux logic and state management
 * for ActivityCard. Renders the pure UI component with appropriate props.
 */
const ActivityCardContainer = ({
  activity,
  agenda,
  changeViewData,
  setActivityType,
  index = 0,
  simplified = false,
  onEditActivity, // New prop for modal-based editing
  onRemix, // Prop for handling remix completion
  isAccordionOpen, // External accordion state
  onToggleAccordion, // External accordion toggle function
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Timer ref for grace period cleanup
  const gracePeriodTimerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (gracePeriodTimerRef.current) {
        clearTimeout(gracePeriodTimerRef.current);
      }
    };
  }, []);

  // Use external accordion state if provided, otherwise fall back to internal state
  const { isOpen: internalAccordionOpen, onToggle: internalToggleAccordion } =
    useDisclosure();
  const accordionOpen =
    isAccordionOpen !== undefined ? isAccordionOpen : internalAccordionOpen;
  const toggleAccordion = onToggleAccordion || internalToggleAccordion;
  const {
    isOpen: isInsightModalOpen,
    onOpen: openInsightModal,
    onClose: closeInsightModal,
  } = useDisclosure();
  const {
    isOpen: isRemixActivityModalOpen,
    onOpen: openRemixActivityModal,
    onClose: closeRemixActivityModal,
  } = useDisclosure();
  const {
    isOpen: isRemixLimitModalOpen,
    onOpen: openRemixLimitModal,
    onClose: closeRemixLimitModal,
  } = useDisclosure();
  const [goLive, { isLoading: isGoingLive }] = useGoLiveMutation();
  const [stopLive, { isLoading: isStoppingLive }] = useStopLiveMutation();
  const [remixActivity, { isLoading: isRemixing }] = useRemixActivityMutation();
  const [deleteActivity, { isLoading: isDeletingActivity }] =
    useDeleteActivityMutation();
  const [updateActivity] = useUpdateActivityMutation();

  const dispatch = useDispatch();
  const { showSuccessToast, showErrorToast } = useToastNotification();

  const liveActivity = useSelector((state) => state.activity.liveActivity);
  const hasLiveActivity = useMemo(
    () => !!Object.keys(liveActivity).length,
    [liveActivity],
  );
  const liveTools = useSelector((state) => state.activity.liveTools);
  const hasLiveTools = useMemo(
    () => !!Object.keys(liveTools).length,
    [liveTools],
  );

  // Get meeting participants for attendee detail view
  const meetingParticipants = useSelector(selectMeetingParticipants);
  const meetingHost = useSelector(selectMeetingHost);

  // Filter out the host from participants list
  const filteredParticipants = useMemo(() => {
    if (!meetingParticipants || !meetingHost) return meetingParticipants;

    // Filter out the host by comparing user IDs
    return meetingParticipants.filter(
      (participant) => participant._id !== meetingHost.userId,
    );
  }, [meetingParticipants, meetingHost]);

  const activityTitle = activityTitles[activity.type];
  const isActivityEditable =
    activity.endTime || activity.live || agenda.endTime;

  // Check if Share activity has links
  const hasLinks =
    activity.type === activityTypes.share &&
    activity.link &&
    activity.link.trim() !== "";

  const startActivity = useCallback(async () => {
    try {
      await goLive(activity._id).unwrap();
      if (!simplified) {
        toggleAccordion();
      }

      // For Share activities, trigger grace period after they're started
      // Skip grace period in E2E test environment for faster test execution
      if (activity.type === activityTypes.share) {
        const isE2EEnvironment =
          process.env.REACT_APP_ENV === "e2e" ||
          process.env.REACT_APP_NAV_ENV === "E2E_TEST";

        if (!isE2EEnvironment) {
          // Add activity to grace period to keep it in current location
          dispatch(addToGracePeriod({ activityId: activity._id }));

          // Clear any existing timer for this activity
          if (gracePeriodTimerRef.current) {
            clearTimeout(gracePeriodTimerRef.current);
          }

          // Start 30-second timer to remove from grace period
          gracePeriodTimerRef.current = setTimeout(() => {
            dispatch(removeFromGracePeriod({ activityId: activity._id }));
          }, 30000);
        }
      }

      // Show appropriate toast message based on activity type and link presence
      let toastMessage;
      if (activity.type === activityTypes.share) {
        toastMessage = hasLinks
          ? "Link share is now running"
          : "Note is now running";
      } else {
        toastMessage = `${activityTitle} is now running`;
      }

      showSuccessToast(toastMessage);
    } catch (error) {
      showErrorToast(error.data?.message || `Error starting ${activityTitle}`);
    }
  }, [
    activityTitle,
    activity._id,
    activity.type,
    hasLinks,
    goLive,
    dispatch,
    showSuccessToast,
    showErrorToast,
    simplified,
    toggleAccordion,
  ]);

  const stopActivity = useCallback(async () => {
    try {
      // Call the API to stop the activity (this sets endTime on backend)
      await stopLive(activity._id).unwrap();
      setIsDialogOpen(false);

      // Skip grace period in E2E test environment for faster test execution
      const isE2EEnvironment =
        process.env.REACT_APP_ENV === "e2e" ||
        process.env.REACT_APP_NAV_ENV === "E2E_TEST";

      if (!isE2EEnvironment) {
        // Add activity to grace period to keep it in current location
        dispatch(addToGracePeriod({ activityId: activity._id }));

        // Clear any existing timer for this activity
        if (gracePeriodTimerRef.current) {
          clearTimeout(gracePeriodTimerRef.current);
        }

        // Start 30-second timer to remove from grace period
        gracePeriodTimerRef.current = setTimeout(() => {
          dispatch(removeFromGracePeriod({ activityId: activity._id }));
        }, 30000);
      }

      // Show success message for stopping
      showSuccessToast(`${activityTitle} has ended`);
    } catch (error) {
      showErrorToast(error.data?.message || `Error ending ${activityTitle}`);
    }
  }, [
    activityTitle,
    activity._id,
    stopLive,
    dispatch,
    showSuccessToast,
    showErrorToast,
  ]);

  const showStopModal = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handlePlayClick = useCallback(() => {
    if (activity.live) {
      setIsDialogOpen(true); // Show confirmation dialog for stopping
    } else {
      startActivity();
    }
  }, [activity.live, startActivity]);

  const handleMoreOptionsClick = useCallback(
    (event) => {
      if (!isDropdownOpen) {
        // Calculate dropdown position
        const rect = event.currentTarget.getBoundingClientRect();
        const position = {
          x: rect.right - 133, // Align dropdown right edge with button right edge
          y: rect.bottom + 4, // Position below button with small gap
        };
        setDropdownPosition(position);
        setIsDropdownOpen(true);
      }
    },
    [isDropdownOpen],
  );

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const handleEditClick = useCallback(() => {
    // Prefer modal-based editing when available
    if (onEditActivity) {
      onEditActivity(activity);
    } else if (changeViewData && setActivityType) {
      // Fallback to old view system for backwards compatibility
      changeViewData.setView(changeViewData.views.add);
      setActivityType(activity.type);
      dispatch(updateSelectedActivity({ activity }));
    }
  }, [onEditActivity, activity, changeViewData, setActivityType, dispatch]);

  const handleInsightClick = useCallback(() => {
    if (activity.insight) {
      openInsightModal();
    }
  }, [activity.insight, openInsightModal]);

  const handleRefineActivity = useCallback(() => {
    closeInsightModal();
    // Open remix modal directly instead of edit modal
    openRemixActivityModal();
  }, [closeInsightModal, openRemixActivityModal]);

  // Function to dismiss remix info - silent background API call
  const dismissRemixInfo = useCallback(async () => {
    // Only proceed if activity has remixInfo that needs to be dismissed
    if (!activity.remixInfo || activity.remixInfo.status !== "needs_remix") {
      return;
    }

    // Make API call to persist the change - API mutation will automatically update local state through fulfilled matcher
    // Fail silently as requested - this is an insignificant background operation
    try {
      await updateActivity({
        activityId: activity._id,
        body: {
          remixInfo: {
            ...activity.remixInfo,
            status: "dismissed",
          },
        },
      }).unwrap();
    } catch (error) {
      // Log error but don't show user notification as requested - fail silently
      console.warn("Failed to dismiss remix info:", error);
    }
  }, [activity, updateActivity]);

  const handleSkipInsight = useCallback(() => {
    dismissRemixInfo();
    closeInsightModal();
  }, [dismissRemixInfo, closeInsightModal]);

  // Handle remix initiation from dropdown - go straight to remix form
  const handleRemixClick = useCallback(() => {
    openRemixActivityModal();
  }, [openRemixActivityModal]);

  // Handle remix completion from modal
  const handleRemixComplete = useCallback(
    async (remixData) => {
      if (remixData && remixData.activity) {
        try {
          // Extract the needed data for the API call
          const { activity: activityFromModal, ...inputs } = remixData;

          const payload = {
            agendaId: agenda._id,
            hostRefinements: inputs.hostRefinements || "",
            keepActivityType: inputs.keepActivityType || false,
          };

          const response = await remixActivity({
            activityId: activity._id,
            body: payload,
          }).unwrap();

          showSuccessToast("Activity remixed successfully!");
          closeRemixActivityModal();

          // Extract activities from response and update the UI
          const updatedActivities = response.activities;
          if (updatedActivities && Array.isArray(updatedActivities)) {
            // Call parent onRemix with the updated activities for additional handling
            onRemix?.(updatedActivities);
          }
        } catch (error) {
          console.error("Remix failed:", error);

          // Check if it's a limit exceeded error (403 status)
          if (
            error?.status === 403 ||
            error?.data?.message?.includes("limit exceeded")
          ) {
            closeRemixActivityModal();
            openRemixLimitModal();
            return;
          }

          showErrorToast("Failed to remix activity. Please try again.");
        }
      }
    },
    [
      activity._id,
      agenda._id,
      remixActivity,
      showSuccessToast,
      showErrorToast,
      closeRemixActivityModal,
      onRemix,
      openRemixLimitModal,
    ],
  );

  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await deleteActivity(activity._id).unwrap();
      showSuccessToast(`${activityTitle} deleted successfully`);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      showErrorToast(error.data?.message || `Error deleting ${activityTitle}`);
      console.error(`Error deleting activity:`, error);
    }
  }, [
    activity._id,
    activityTitle,
    deleteActivity,
    showSuccessToast,
    showErrorToast,
  ]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  // Determine if the play button should be disabled
  const isPlayDisabled = useMemo(() => {
    if (simplified) {
      return !agenda?.live && isActivityEditable;
    }

    const hasOtherLiveActivity =
      activity.type === "share" ? hasLiveTools : hasLiveActivity;
    return !agenda?.live || hasOtherLiveActivity || isActivityEditable;
  }, [
    simplified,
    agenda?.live,
    isActivityEditable,
    activity.type,
    hasLiveTools,
    hasLiveActivity,
  ]);

  return (
    <>
      {/* Confirmation dialog for stopping activity */}
      <AlertDialog
        title={`End ${activity.displayName}`}
        headerIcon={getDisplayIcon(activity)}
        activityType={activity.type}
        activity={activity}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        primaryButton={
          <Button
            size="sm"
            colorScheme="red"
            onClick={stopActivity}
            ml={3}
            disabled={isStoppingLive}
            data-testid="activity-end-button"
          >
            End
          </Button>
        }
        secondaryButton={
          <Button
            size="sm"
            onClick={() => setIsDialogOpen(false)}
            bg="white"
            disabled={isStoppingLive}
          >
            Cancel
          </Button>
        }
      >
        Are you sure you want to end this activity?
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        title={`Delete ${activity.displayName}`}
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        primaryButton={
          <Button
            size="sm"
            colorScheme="red"
            onClick={handleDeleteConfirm}
            isLoading={isDeletingActivity}
            ml={3}
          >
            Delete
          </Button>
        }
        secondaryButton={
          <Button
            size="sm"
            onClick={handleDeleteCancel}
            bg="white"
            disabled={isDeletingActivity}
          >
            Cancel
          </Button>
        }
      >
        Are you sure you want to delete this activity?
      </AlertDialog>

      {/* Pure UI component */}
      <ActivityCardUI
        activity={activity}
        index={index}
        // Required props for getLiveButtonProps
        agenda={agenda}
        meetingParticipants={filteredParticipants}
        hasLiveActivity={hasLiveActivity}
        isGoingLive={isGoingLive}
        startActivity={startActivity}
        stopActivity={showStopModal}
        simplified={simplified}
        showAccordion={!simplified}
        isAccordionOpen={accordionOpen}
        onPlayClick={handlePlayClick}
        onMoreOptionsClick={handleMoreOptionsClick}
        onToggleAccordion={toggleAccordion}
        isLoading={isGoingLive || isStoppingLive}
        isRemixing={isRemixing}
        isDisabled={isPlayDisabled}
        // Dropdown props
        isDropdownOpen={isDropdownOpen}
        onDropdownClose={handleDropdownClose}
        dropdownPosition={dropdownPosition}
        onInsight={handleInsightClick}
        onRemix={handleRemixClick}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        isActivityEditable={isActivityEditable}
      />

      {/* Insight Modal */}
      {activity.insight && (
        <InsightModal
          isOpen={isInsightModalOpen}
          onClose={closeInsightModal}
          insight={activity.insight}
          onRefineActivity={handleRefineActivity}
          onSkip={handleSkipInsight}
          isRefining={isRemixing}
        />
      )}

      {/* Remix Activity Modal - Detailed remixing modal */}
      <RemixActivityModal
        isOpen={isRemixActivityModalOpen}
        onClose={closeRemixActivityModal}
        onRemixComplete={handleRemixComplete}
        activity={activity}
        agenda={agenda}
        isLoading={isRemixing}
      />

      {/* Remix Limit Modal - Show when user hits remix limit */}
      <RemixLimitModal
        isOpen={isRemixLimitModalOpen}
        onClose={closeRemixLimitModal}
      />
    </>
  );
};

export default ActivityCardContainer;
